(function () {
  'use strict';

  const KEY_NEW = 'oatmeal_wishlist_items_v1';
  const KEY_OLD = 'oatmeal_wishlist_variants';

  let cache = loadLocal();
  let cacheSignature = signature(cache);
  const variantRegistry = new Map();

  function emitChange() {
    window.dispatchEvent(new CustomEvent('oatmeal:wishlist:changed'));
  }

  function toId(value) {
    const raw = String(value == null ? '' : value).trim();
    if (!raw) return '';
    return /^\d+$/.test(raw) ? raw : raw;
  }

  function toText(value) {
    return String(value == null ? '' : value).trim();
  }

  function normalizeItem(item) {
    if (typeof item === 'number' || typeof item === 'string') {
      return {
        handle: '',
        color: '',
        productId: '',
        variantId: toId(item),
        productUrl: '',
      };
    }

    if (!item || typeof item !== 'object') {
      return {
        handle: '',
        color: '',
        productId: '',
        variantId: '',
        productUrl: '',
      };
    }

    const handle = toText(item.handle);
    const color = toText(item.color);
    const productId = toId(item.productId || item.product_id);
    const variantId = toId(item.variantId || item.variant_id);
    const productUrl = toText(item.productUrl || item.product_url);

    return { handle, color, productId, variantId, productUrl };
  }

  function makeLookupKey(item) {
    if (item.handle && item.color) return `hc:${item.handle.toLowerCase()}|||${item.color.toLowerCase()}`;
    if (item.variantId) return `v:${item.variantId}`;
    if (item.productId) return `p:${item.productId}`;
    return '';
  }

  function isUsable(item) {
    return !!(item.handle && item.color) || !!item.variantId || !!item.productId;
  }

  function signature(items) {
    return (items || [])
      .map((item) => {
        const normalized = normalizeItem(item);
        return [
          normalized.handle.toLowerCase(),
          normalized.color.toLowerCase(),
          normalized.productId,
          normalized.variantId,
        ].join('|||');
      })
      .join('@@');
  }

  function uniqueItems(items) {
    const out = [];
    const seen = new Set();

    for (const raw of items || []) {
      const item = normalizeItem(raw);
      if (!isUsable(item)) continue;

      const key = makeLookupKey(item);
      if (!key || seen.has(key)) continue;
      seen.add(key);

      out.push(item);
    }

    return out;
  }

  function loadLocal() {
    try {
      const raw = localStorage.getItem(KEY_NEW);
      const arr = raw ? JSON.parse(raw) : [];
      return uniqueItems(Array.isArray(arr) ? arr : []);
    } catch (_) {
      return [];
    }
  }

  function persist(nextItems, { silent = false } = {}) {
    cache = uniqueItems(nextItems);
    const nextSignature = signature(cache);

    try {
      localStorage.setItem(KEY_NEW, JSON.stringify(cache));
    } catch (_) {}

    if (nextSignature !== cacheSignature) {
      cacheSignature = nextSignature;
      if (!silent) emitChange();
    }
  }

  function cleanupLegacy() {
    try {
      localStorage.getItem(KEY_OLD);
    } catch (_) {}
  }

  function getVariantIdSet() {
    const set = new Set();
    for (const item of cache) {
      if (item.variantId) set.add(item.variantId);
    }
    return set;
  }

  function register(item) {
    const normalized = normalizeItem(item);
    if (!isUsable(normalized)) return normalized;

    if (normalized.variantId) {
      variantRegistry.set(normalized.variantId, normalized);
    }

    return normalized;
  }

  function matchesQuery(item, query) {
    if (!item || !query) return false;

    if (query.variantId && item.variantId && query.variantId === item.variantId) return true;

    if (query.handle && query.color && item.handle && item.color) {
      return query.handle.toLowerCase() === item.handle.toLowerCase() && query.color.toLowerCase() === item.color.toLowerCase();
    }

    if (query.productId && item.productId && query.productId === item.productId) {
      if (!query.color || !item.color) return true;
      return query.color.toLowerCase() === item.color.toLowerCase();
    }

    return false;
  }

  function findLocalMatch(query) {
    return cache.find((item) => matchesQuery(item, query)) || null;
  }

  function has(arg1, arg2) {
    if (!isLoggedIn()) return false;

    const query =
      typeof arg1 === 'object' && arg1 !== null ? register(arg1) : normalizeItem({ handle: arg1, color: arg2 });

    if (!isUsable(query)) return false;

    return !!findLocalMatch(query);
  }

  function upsertLocal(item, { silent = false } = {}) {
    const normalized = register(item);
    if (!isUsable(normalized)) return normalized;

    const next = cache.slice();
    const existingIndex = next.findIndex((entry) => matchesQuery(entry, normalized));

    if (existingIndex >= 0) {
      next[existingIndex] = {
        ...next[existingIndex],
        ...normalized,
      };
    } else {
      next.unshift(normalized);
    }

    persist(next, { silent });

    return normalized;
  }

  function removeLocal(query, { silent = false } = {}) {
    const normalized =
      typeof query === 'object' && query !== null ? normalizeItem(query) : normalizeItem({ handle: query, color: '' });

    let removed = null;
    const next = cache.filter((item) => {
      const match = matchesQuery(item, normalized);
      if (match && !removed) removed = item;
      return !match;
    });

    persist(next, { silent });

    return removed;
  }

  function add(item) {
    return upsertLocal(item);
  }

  function remove(arg1, arg2) {
    const query =
      typeof arg1 === 'object' && arg1 !== null ? arg1 : { handle: arg1, color: arg2 };
    return removeLocal(query);
  }

  function list() {
    if (!isLoggedIn()) return [];
    return cache.slice();
  }

  function parseHandleFromUrl(url) {
    const raw = toText(url);
    if (!raw) return '';

    try {
      const parsed = new URL(raw, window.location.origin);
      const match = parsed.pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (_) {
      const match = raw.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    }
  }

  function isLoggedIn() {
    return window.OATMEAL_IS_LOGGED_IN === true;
  }

  function getVariantCompatMeta(variantId) {
    const id = toId(variantId);
    if (!id) return null;

    return variantRegistry.get(id) || findLocalMatch({ handle: '', color: '', productId: '', variantId: id }) || null;
  }

  function compatHas(variantId) {
    const id = toId(variantId);
    if (!id) return false;
    return has({ variantId: id });
  }

  function compatAdd(value) {
    if (typeof value === 'object' && value !== null) {
      return add(value);
    }

    const meta = getVariantCompatMeta(value);
    if (!meta) return null;
    return add(meta);
  }

  function compatRemove(value) {
    if (typeof value === 'object' && value !== null) {
      return remove(value);
    }

    const meta = getVariantCompatMeta(value);
    if (!meta) return null;
    return remove(meta);
  }

  cleanupLegacy();

  window.OatmealWishlistStore = {
    add,
    remove,
    has,
    list,
    register,
    parseHandleFromUrl,
    syncRemote: () => Promise.resolve(),
    _key: KEY_NEW,
  };

  window.OatmealWishlist = {
    has: compatHas,
    add: compatAdd,
    remove: compatRemove,
    register,
  };
})();
